import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import {
  RedColorPath,
  BlueColorPath,
  GreenColorPath,
  YellowColorPath,
  SAFE_CELLS,
  Coordinate,
} from "../utils/ludoPaths";

// Type definitions for the Ludo game
export type PlayerColor = "red" | "blue" | "yellow" | "green";
export type TokenStatus = "home" | "onpath" | "finished" | "returning";

/**
 * Represents a game token with its current state and position
 */
export interface Token {
  id: number; // Unique identifier (0-3)
  status: TokenStatus; // Current state of the token
  pathIndex: number; // Position on the path (-1 for home, 0+ for path positions)
  lastMovedAt: number | null; // Timestamp of last movement
}

/**
 * Main state interface for the Ludo game
 */
export interface LudoState {
  players: PlayerColor[]; // Array of player colors in turn order
  paths: Record<PlayerColor, Coordinate[]>; // Movement paths for each player
  tokens: Record<PlayerColor, Token[]>; // Tokens for each player
  currentPlayerIndex: number; // Index of current player in players array
  dice: number | null; // Current dice value (null if not rolled)
  canRoll: boolean; // Whether current player can roll dice
  winners: PlayerColor[]; // Array of players who have won
  lastAction: any; // Last action performed (for debugging)
  capturedToken: boolean; // Flag if a token was captured this turn
  tokenReachedHome: boolean; // Flag if a token reached home this turn
  consecutiveSixes: number; // Count of consecutive sixes rolled (max 3)
  isReturnInProgress: boolean; // Flag if a token return animation is in progress
}

// Player colors in turn order (clockwise: red, blue, yellow, green)
const PLAYERS: PlayerColor[] = ["red", "blue", "yellow", "green"];

// Coordinates where tokens enter their home stretch (final path segment)
const HOME_TURN_COORDS: Record<PlayerColor, Coordinate> = {
  red: [7, 0],
  blue: [0, 7],
  yellow: [7, 14],
  green: [14, 7],
};

/**
 * Creates initial tokens for a player (all in home position)
 */
const createTokens = (): Token[] =>
  Array.from({ length: 4 }).map((_, i) => ({
    id: i,
    status: "home" as TokenStatus,
    pathIndex: -1, // -1 indicates token is in home, not on path
    lastMovedAt: null,
  }));

// Predefined movement paths for each player color
const PATHS: Record<PlayerColor, Coordinate[]> = {
  red: RedColorPath,
  blue: BlueColorPath,
  green: GreenColorPath,
  yellow: YellowColorPath,
};

/**
 * Checks if two coordinates are the same
 */
const sameCoord = (
  a: Coordinate | undefined,
  b: Coordinate | undefined
): boolean => !!(a && b && a[0] === b[0] && a[1] === b[1]);

/**
 * Checks if a cell is a safe cell (where tokens cannot be captured)
 */
const isSafeCell = (row: number, col: number): boolean =>
  SAFE_CELLS.some((c) => c[0] === row && c[1] === col);

/**
 * Initial game state
 */
const initialState: LudoState = {
  players: PLAYERS,
  paths: { ...PATHS }, // Copy paths to avoid mutation
  tokens: {
    red: createTokens(),
    blue: createTokens(),
    green: createTokens(),
    yellow: createTokens(),
  },
  currentPlayerIndex: 0, // Start with first player (red)
  dice: null, // No dice rolled initially
  canRoll: true, // First player can roll
  winners: [], // No winners initially
  lastAction: null, // No actions performed yet
  capturedToken: false, // No captures initially
  tokenReachedHome: false, // No tokens home initially
  consecutiveSixes: 0, // No consecutive sixes initially
  isReturnInProgress: false, // No return animations initially
};

/**
 * Async thunk to roll the dice
 */
// export const rollDice = createAsyncThunk(
//   'ludo/rollDice',
//   async (_, { getState, dispatch }) => {
//     // Generate random dice value (1-6)
//     const diceValue = Math.floor(Math.random() * 6) + 1;
//     const state = (getState() as { ludo: LudoState }).ludo;

//     // Check if this is the 3rd consecutive six (special rule)
//     if (diceValue === 6 && state.consecutiveSixes === 2) {
//       // Third six in a row - turn ends automatically
//       dispatch(setDice(null));
//       dispatch(endTurn());
//       return diceValue;
//     }

//     return diceValue;
//   },
// );

// export const rollDice = createAsyncThunk(
//   "ludo/rollDice",
//   async (_, { getState, dispatch }) => {
//     // Generate random dice value (1-6)
//     const diceValue = Math.floor(Math.random() * 6) + 1;
//     const state = (getState() as { ludo: LudoState }).ludo;
//     // Check if this is the 3rd consecutive six (special rule)
//     if (diceValue === 6 && state.consecutiveSixes === 2) {
//       // Third six in a row - turn ends automatically
//       const diceValue = Math.floor(Math.random() * 5) + 1;
//       // dispatch(setDice(null));
//       // dispatch(endTurn());
//       return diceValue;
//     }
//     return diceValue;
//   }
// );

export const rollDice = createAsyncThunk(
  "ludo/rollDice",
  async (_, { getState, dispatch }) => {
    const state = (getState() as { ludo: LudoState }).ludo;
    const currentColor = state.players[state.currentPlayerIndex];
    const allTokensHome = state.tokens[currentColor]?.every((token) => token.status === "home") ?? false;

    let diceValue: number;

    if (allTokensHome) {
      // Bias towards 6 when all tokens are home: ~40% chance for 6 (vs. standard 16.7%), rest uniform 1-5
      const weightedRoll = Math.random();
      if (weightedRoll < 0.4) {
        diceValue = 6;
      } else {
        diceValue = Math.floor(Math.random() * 5) + 1;
      }
    } else {
      // Standard uniform roll for normal play
      diceValue = Math.floor(Math.random() * 6) + 1;
    }

    // Check for 3rd consecutive six (reroll to 1-5 and end turn)
    if (diceValue === 6 && state.consecutiveSixes === 2) {
      diceValue = Math.floor(Math.random() * 5) + 1;
      // Optionally uncomment to auto-end turn on 3rd six:
      // dispatch(setDice(null));
      // dispatch(endTurn());
    }

    return diceValue;
  }
);

/**
 * Utility function to delay execution (for animations)
 */
const sleep = (ms: number) => new Promise((r) => setTimeout(r as any, ms));

/**
 * Payload interface for moving token steps
 */
interface MoveTokenStepsPayload {
  color: PlayerColor;
  tokenId: number;
  steps: number | "bringOut"; // Number of steps or 'bringOut' to enter board
}

/**
 * Async thunk to move a token multiple steps with animation delays
 * Handles both bringing tokens out and moving on path
 */
export const moveTokenSteps = createAsyncThunk(
  "ludo/moveTokenSteps",
  async (
    { color, tokenId, steps }: MoveTokenStepsPayload,
    { getState, dispatch }
  ) => {
    const state = (getState() as { ludo: LudoState }).ludo;
    const path = state.paths[color];
    const token = state.tokens[color]?.find((t) => t.id === tokenId);
    if (!token) return;

    // Reset flags before move
    dispatch(setCapturedToken(false));
    dispatch(setTokenReachedHome(false));

    // Handle bringing token out of home
    if (token.status === "home") {
      if (steps !== "bringOut") return;
      dispatch(moveOneStep({ color, tokenId, bringOut: true }));
      await sleep(240); // Animation delay
      await dispatch(checkCaptures({ color, tokenId }));
      return;
    }

    // Handle moving token on path
    if (token.status !== "onpath") return;
    if (typeof steps !== "number") return;

    // Move step by step with animations
    for (let i = 0; i < steps; i++) {
      const st = (getState() as { ludo: LudoState }).ludo;
      const tkn = st.tokens[color]?.find((t) => t.id === tokenId);
      if (!tkn || tkn.status !== "onpath") break;
      if (tkn.pathIndex >= (st.paths[color]?.length ?? 0) - 1) break;

      dispatch(moveOneStep({ color, tokenId, bringOut: false }));
      await sleep(200); // Animation delay between steps
    }

    // Check if token reached home (finished the path)
    const finalState = (getState() as { ludo: LudoState }).ludo;
    const finalToken = finalState.tokens[color]?.find((t) => t.id === tokenId);
    if (finalToken && finalToken.status === "finished") {
      dispatch(setTokenReachedHome(true));
    }

    // Check for captures after movement
    await dispatch(checkCaptures({ color, tokenId }));
  }
);

/**
 * Payload interface for capture checking
 */
interface CheckCapturesPayload {
  color: PlayerColor;
  tokenId: number;
}

/**
 * Async thunk to check if a token captures any opponent tokens
 * Captured tokens are sent back to their home
 */
export const checkCaptures = createAsyncThunk(
  "ludo/checkCaptures",
  async ({ color, tokenId }: CheckCapturesPayload, { getState, dispatch }) => {
    const state = (getState() as { ludo: LudoState }).ludo;
    const path = state.paths[color];
    const token = state.tokens[color]?.find((t) => t.id === tokenId);
    if (!token || token.status !== "onpath") return;

    const landed = path[token.pathIndex];
    // No captures on safe cells
    if (isSafeCell(landed[0], landed[1])) return;

    const returnPromises: Promise<any>[] = [];
    let capturedAny = false;

    // Check all opponent tokens for captures
    for (const otherColor of state.players) {
      if (otherColor === color) continue; // Skip own tokens

      // Find all tokens of otherColor currently at the landed coordinate
      const opponentTokensAtLanded = (state.tokens[otherColor] ?? []).filter(
        (otherToken) => {
          if (otherToken.status === "onpath") {
            const oc = state.paths[otherColor]?.[otherToken.pathIndex];
            return sameCoord(oc, landed);
          }
          return false;
        }
      );

      // If the opponent has 2 or more tokens on this cell, they form a joint/block (safe joint like in Ludo King) and cannot be captured
      if (opponentTokensAtLanded.length >= 2) {
        continue;
      }

      for (const otherToken of opponentTokensAtLanded) {
        // Capture found - send opponent token back home
        capturedAny = true;
        dispatch(
          startReturn({ color: otherColor, tokenId: otherToken.id })
        );
        const returnSteps = otherToken.pathIndex;
        const promise = dispatch(
          processReturnSteps({
            color: otherColor,
            tokenId: otherToken.id,
            steps: returnSteps,
          })
        );
        returnPromises.push(promise);
      }
    }

    // If any captures occurred, set flag to keep turn
    if (returnPromises.length > 0) {
      await Promise.all(returnPromises);
      dispatch(setCapturedToken(true));
    }
  }
);

/**
 * Payload interface for return steps processing
 */
interface ProcessReturnStepsPayload {
  color: PlayerColor;
  tokenId: number;
  steps: number;
}

/**
 * Async thunk to animate a captured token returning home
 * Moves backwards along the path step by step
 */
export const processReturnSteps = createAsyncThunk(
  "ludo/processReturnSteps",
  async (
    { color, tokenId, steps }: ProcessReturnStepsPayload,
    { dispatch, getState }
  ) => {
    // Return along the path backwards, one step at a time
    for (let i = 0; i < steps; i++) {
      const state = (getState() as { ludo: LudoState }).ludo;
      const token = state.tokens[color]?.find((t) => t.id === tokenId);
      if (!token || token.pathIndex <= 0) break;

      dispatch(returnOneStep({ color, tokenId }));
      await sleep(150); // Animation delay
    }
    // After reaching start of path, send token home
    dispatch(finishReturn({ color, tokenId }));
    await sleep(200); // Final animation delay
  }
);

// Payload interfaces for various actions
interface MoveOneStepPayload {
  color: PlayerColor;
  tokenId: number;
  bringOut: boolean;
}

interface BasicTokenPayload {
  color: PlayerColor;
  tokenId: number;
}

interface CheckPlayerWinPayload {
  color: PlayerColor;
}

interface SetTokenDirectPayload {
  color: PlayerColor;
  tokenId: number;
  status: TokenStatus;
  pathIndex: number;
}

/**
 * Main Redux slice for Ludo game state management
 */
const slice = createSlice({
  name: "ludo",
  initialState,
  reducers: {
    /**
     * Reset the game to initial state
     */
    initGame: (state, action: PayloadAction<PlayerColor[] | void>) => {
      const activePlayers = action.payload || PLAYERS;
      state.players = activePlayers;
      // @ts-ignore
      const tokens: Record<PlayerColor, Token[]> = {};
      activePlayers.forEach((color) => {
        tokens[color] = createTokens();
      });
      state.tokens = tokens;
      state.paths = { ...PATHS };
      state.currentPlayerIndex = 0;
      state.dice = null;
      state.canRoll = true;
      state.winners = [];
      state.lastAction = "init";
      state.capturedToken = false;
      state.tokenReachedHome = false;
      state.consecutiveSixes = 0;
    },

    /**
     * Set the dice value and track consecutive sixes
     */
    setDice(state, action: PayloadAction<number | null>) {
      state.dice = action.payload;
      // Track consecutive sixes for special rule
      if (action.payload === 6) {
        state.consecutiveSixes += 1;
      } else if (action.payload !== null) {
        // Reset counter if non-six is rolled
        state.consecutiveSixes = 0;
      }
      state.lastAction = { type: "rolled", value: action.payload };
    },

    /**
     * Move a token one step forward (either bring out or move on path)
     */
    moveOneStep(state, action: PayloadAction<MoveOneStepPayload>) {
      const { color, tokenId, bringOut } = action.payload;
      const token = state.tokens[color]?.find((t) => t.id === tokenId);
      if (!token) return;

      if (bringOut) {
        // Bring token out of home to start of path
        token.status = "onpath";
        token.pathIndex = 0;
      } else if (token.status === "onpath") {
        // Move token forward on path
        const path = state.paths[color];
        const targetIndex = Math.min(
          token.pathIndex + 1,
          (path?.length ?? 0) - 1
        );
        token.pathIndex = targetIndex;
        // Check if token reached the end
        if (token.pathIndex === (path?.length ?? 0) - 1)
          token.status = "finished";
      }
      token.lastMovedAt = Date.now();
      state.lastAction = {
        type: "movedOne",
        color,
        tokenId,
        pathIndex: token.pathIndex,
      };
    },

    setCurrentPlayerIndex: (state, action: PayloadAction<number>) => {
      state.currentPlayerIndex = action.payload;
    },

    /**
     * Start the return process for a captured token
     */
    startReturn(state, action: PayloadAction<BasicTokenPayload>) {
      const { color, tokenId } = action.payload;
      const token = state.tokens[color]?.find((t) => t.id === tokenId);
      if (!token) return;
      token.status = "returning";
      state.lastAction = { type: "startReturn", color, tokenId };
      state.isReturnInProgress = true;
    },

    /**
     * Move a returning token one step backward
     */
    returnOneStep(state, action: PayloadAction<BasicTokenPayload>) {
      const { color, tokenId } = action.payload;
      const token = state.tokens[color]?.find((t) => t.id === tokenId);
      if (!token) return;

      if (token.pathIndex > 0) {
        token.pathIndex = token.pathIndex - 1;
        token.status = "returning";
      }

      token.lastMovedAt = Date.now();
      state.lastAction = {
        type: "returnStep",
        color,
        tokenId,
        pathIndex: token.pathIndex,
      };
    },

    /**
     * Finish returning a token (send it home)
     */
    finishReturn(state, action: PayloadAction<BasicTokenPayload>) {
      const { color, tokenId } = action.payload;
      const token = state.tokens[color]?.find((t) => t.id === tokenId);
      if (!token) return;
      token.status = "home";
      token.pathIndex = -1;
      token.lastMovedAt = Date.now();
      state.lastAction = { type: "finishedReturn", color, tokenId };
      state.isReturnInProgress = false;
    },

    /**
     * End current player's turn and move to next player
     * Skips players who have already won
     */
    endTurn(state) {
      const num = state.players.length;
      // Find next player who hasn't won
      for (let i = 1; i <= num; i++) {
        const idx = (state.currentPlayerIndex + i) % num;
        const color = state.players[idx];
        const hasWinner = state.winners.includes(color);
        if (!hasWinner) {
          state.currentPlayerIndex = idx;
          break;
        }
      }
      state.dice = null;
      state.canRoll = true;
      state.lastAction = "endTurn";
      state.capturedToken = false;
      state.tokenReachedHome = false;
      state.consecutiveSixes = 0; // Reset consecutive sixes on turn end
    },

    /**
     * Set whether player can roll dice
     */
    setCanRoll(state, action: PayloadAction<boolean>) {
      state.canRoll = action.payload;
    },

    /**
     * Check if a player has won (all tokens finished)
     */
    checkPlayerWin(state, action: PayloadAction<CheckPlayerWinPayload>) {
      const { color } = action.payload;
      const allFin = (state.tokens[color] ?? []).every(
        (t) => t.status === "finished"
      );
      if (allFin && !state.winners.includes(color)) {
        state.winners.push(color);
      }
    },

    /**
     * Set captured token flag (gives player another turn)
     */
    setCapturedToken(state, action: PayloadAction<boolean>) {
      state.capturedToken = action.payload;
    },

    /**
     * Set token reached home flag (gives player another turn)
     */
    setTokenReachedHome(state, action: PayloadAction<boolean>) {
      state.tokenReachedHome = action.payload;
    },

    /**
     * Directly set token status and position (for debugging/testing)
     */
    setTokenDirect(state, action: PayloadAction<SetTokenDirectPayload>) {
      const { color, tokenId, status, pathIndex } = action.payload;
      const token = state.tokens[color]?.find((t) => t.id === tokenId);
      if (!token) return;
      token.status = status;
      token.pathIndex = pathIndex;
      token.lastMovedAt = Date.now();
    },
  },

  // Handle async thunk results
  extraReducers: (builder) => {
    builder.addCase(rollDice.fulfilled, (state, action) => {
      state.dice = action.payload;
      state.canRoll = false;
      // Track consecutive sixes
      if (action.payload === 6) {
        state.consecutiveSixes += 1;
      } else {
        state.consecutiveSixes = 0;
      }
      state.lastAction = { type: "rolled", value: action.payload };
    });
  },
});

// Export all actions
export const {
  initGame,
  setDice,
  moveOneStep,
  setCurrentPlayerIndex,
  startReturn,
  returnOneStep,
  finishReturn,
  endTurn,
  setCanRoll,
  checkPlayerWin,
  setCapturedToken,
  setTokenReachedHome,
  setTokenDirect,
} = slice.actions;

// Export the reducer
export default slice.reducer;
